import type { Point } from '@/core/geometry'

export enum ThemeMode {
  System = 'system',
  Light = 'light',
  Dark = 'dark',
}

export enum BottomSheetSnap {
  Peek = 'peek',
  Half = 'half',
  Full = 'full',
}

interface BottomSheetSnapSize {
  viewportHeightFactor: number
  rootFontSizeOffset: number
}

const BOTTOM_SHEET_SNAP_SIZES = {
  [BottomSheetSnap.Peek]: { viewportHeightFactor: 0, rootFontSizeOffset: 5.5 },
  [BottomSheetSnap.Half]: { viewportHeightFactor: 0.48, rootFontSizeOffset: 0 },
  [BottomSheetSnap.Full]: { viewportHeightFactor: 1, rootFontSizeOffset: -4 },
} as const satisfies Readonly<Record<BottomSheetSnap, BottomSheetSnapSize>>

export const BOTTOM_SHEET_SNAP_TRANSITION = {
  durationMs: 200,
  timingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const

function bottomSheetSnapSize(snap: BottomSheetSnap): BottomSheetSnapSize {
  const size = BOTTOM_SHEET_SNAP_SIZES[snap]
  if (!size) throw new Error(`Bottom sheet snap "${String(snap)}" has no configured size`)
  return size
}

export function bottomSheetSnapHeightCss(snap: BottomSheetSnap): string {
  const { viewportHeightFactor, rootFontSizeOffset } = bottomSheetSnapSize(snap)
  if (viewportHeightFactor === 0) return `${rootFontSizeOffset}rem`
  if (rootFontSizeOffset === 0) return `${viewportHeightFactor * 100}dvh`
  const operator = rootFontSizeOffset < 0 ? '-' : '+'
  return `calc(${viewportHeightFactor * 100}dvh ${operator} ${Math.abs(rootFontSizeOffset)}rem)`
}

export function bottomSheetSnapHeightPx(
  snap: BottomSheetSnap,
  dynamicViewportHeight: number,
  rootFontSize: number,
): number {
  if (!Number.isFinite(dynamicViewportHeight) || dynamicViewportHeight <= 0) {
    throw new Error('Bottom sheet requires a finite positive dynamic viewport height')
  }
  if (!Number.isFinite(rootFontSize) || rootFontSize <= 0) {
    throw new Error('Bottom sheet requires a finite positive root font size')
  }
  const { viewportHeightFactor, rootFontSizeOffset } = bottomSheetSnapSize(snap)
  return dynamicViewportHeight * viewportHeightFactor + rootFontSize * rootFontSizeOffset
}

export function validateBottomSheetConfiguration(): void {
  const snaps = Object.values(BottomSheetSnap)
  if (Object.keys(BOTTOM_SHEET_SNAP_SIZES).length !== snaps.length) {
    throw new Error('Bottom sheet snap size configuration is incomplete')
  }
  for (const snap of snaps) {
    const size = bottomSheetSnapSize(snap)
    if (!Number.isFinite(size.viewportHeightFactor) || size.viewportHeightFactor < 0
      || !Number.isFinite(size.rootFontSizeOffset)) {
      throw new Error(`Bottom sheet snap "${snap}" has an invalid size`)
    }
  }
  if (!Number.isFinite(BOTTOM_SHEET_SNAP_TRANSITION.durationMs)
    || BOTTOM_SHEET_SNAP_TRANSITION.durationMs <= 0
    || !BOTTOM_SHEET_SNAP_TRANSITION.timingFunction.trim()) {
    throw new Error('Bottom sheet snap transition configuration is invalid')
  }
}

export enum OverlayId {
  Grid = 'grid',
  SafeZones = 'safe-zones',
}

export enum ToolTab {
  Crop = 'crop',
  Adjust = 'adjust',
  Frame = 'frame',
  Collage = 'collage',
  Format = 'format',
  Export = 'export',
}

export enum MorePanelId {
  Frame = 'frame',
  Collage = 'collage',
  Export = 'export',
}

export enum PreviewTab {
  Profile = 'profile',
  Post = 'post',
}

export const PREVIEW_OLDER_POST_OFFSETS = [0, 1, 2] as const
export type PreviewOlderPostOffset = typeof PREVIEW_OLDER_POST_OFFSETS[number]
export type QualityLoupePoint = Point | null
